// The region question (#5881): the one-time "where are you based?" the dashboard
// asks a person whose `users.region` is still null, and the two pure halves of
// it — what to pre-select from the browser's language, and the option list.
//
// The answer decides whether the separate cross-border consent applies (only
// `CN`, mainland China, owes it: `missingConsent`, `@developerz/domain`), so the
// pre-fill is CONSERVATIVE about China in particular. `zh-CN` and a Simplified
// script tag (`zh-Hans*`) pre-select `CN`; a bare `zh`, `zh-TW`, `zh-HK` and a
// Traditional script tag (`zh-Hant*`) never do, because Taiwan, Hong Kong and
// Macau are their own regions and the consent is not theirs to give. Any other
// tag pre-selects the region subtag it names, when ISO assigned it. A pre-fill
// is only a pre-selection: the person confirms or changes it.

import {
  CROSS_BORDER_REGION,
  ISO_3166_1_ALPHA2,
  type RegionCode,
  RegionCodeSchema,
} from '@developerz/domain';

/** The region a BCP-47 tag pre-selects, or `null` when it names none. */
export function prefillRegion(tag: string | undefined): RegionCode | null {
  if (tag === undefined) return null;
  const subtags = tag.trim().split(/[-_]/);
  const language = subtags[0]?.toLowerCase() ?? '';
  const script = subtags.find((s, i) => i > 0 && /^[A-Za-z]{4}$/.test(s))?.toLowerCase();
  const region = subtags.find((s, i) => i > 0 && /^[A-Za-z]{2}$/.test(s))?.toUpperCase();
  if (language === 'zh') {
    if (script === 'hans' || (script === undefined && region === CROSS_BORDER_REGION)) {
      return CROSS_BORDER_REGION;
    }
    // `zh`, `zh-TW`, `zh-HK`, `zh-Hant*`: never mainland China.
    if (region === undefined || region === CROSS_BORDER_REGION) return null;
  }
  const parsed = RegionCodeSchema.safeParse(region);
  return parsed.success ? parsed.data : null;
}

/** The browser's first preferred language, through {@link prefillRegion}. */
export function browserRegionPrefill(): RegionCode | null {
  try {
    return prefillRegion(navigator.languages?.[0] ?? navigator.language);
  } catch {
    return null; // no `navigator` (a non-browser test environment)
  }
}

/** One option of the region picker. */
export type RegionOption = { readonly value: RegionCode; readonly label: string };

/**
 * Every assigned region, named in `locale` by the platform's own `Intl` data and
 * sorted by that name. `CN` takes `cnLabel` instead: `Intl` calls it "China",
 * and the question is precisely whether the person is in MAINLAND China.
 */
export function regionOptions(locale: string, cnLabel: string): RegionOption[] {
  const names = new Intl.DisplayNames([locale], { type: 'region' });
  return ISO_3166_1_ALPHA2.map((code) => ({
    value: code,
    label: code === CROSS_BORDER_REGION ? cnLabel : (names.of(code) ?? code),
  })).sort((a, b) => a.label.localeCompare(b.label, locale));
}
